import { useCallback, useState } from "react";
import { format } from "date-fns";
import { validarFormContaPagar } from "../helpers/conta-pagar-form-payload";
import { criarContaPagar } from "../services/contas-pagar.service";

export type NovaContaPagarForm = {
  descricao: string;
  naturezaDespesa: "FIXA" | "VARIAVEL";
  categoria: string;
  contexto: string;
  eventoId: number | null;
  fornecedorId: string;
  centroCustoId: string;
  vencimento: string;
  competenciaEm: string;
  valorTotal: string;
  observacao: string;
  baseCalculoImposto: string;
  aliquotaImposto: string;
};

export function formPadraoNovaContaPagar(
  partial?: Partial<NovaContaPagarForm>,
): NovaContaPagarForm {
  const hoje = format(new Date(), "yyyy-MM-dd");
  return {
    descricao: "",
    naturezaDespesa: "VARIAVEL",
    categoria: "OUTROS",
    contexto: "OUTROS",
    eventoId: null,
    fornecedorId: "",
    centroCustoId: "",
    vencimento: hoje,
    competenciaEm: hoje,
    valorTotal: "",
    observacao: "",
    baseCalculoImposto: "",
    aliquotaImposto: "11",
    ...partial,
  };
}

export type UseCriarTituloPagarOptions = {
  onSuccess?: () => void;
};

export type CriarTituloPagarFeedback =
  | { severity: "success"; message: string }
  | { severity: "error"; message: string }
  | null;

export function useCriarTituloPagar(options?: UseCriarTituloPagarOptions) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<NovaContaPagarForm>(() =>
    formPadraoNovaContaPagar(),
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<CriarTituloPagarFeedback>(null);

  const abrir = useCallback((partial?: Partial<NovaContaPagarForm>) => {
    setForm(formPadraoNovaContaPagar(partial));
    setError(null);
    setOpen(true);
  }, []);

  const fechar = useCallback(() => {
    if (loading) return;
    setOpen(false);
    setError(null);
  }, [loading]);

  const clearFeedback = useCallback(() => setFeedback(null), []);

  const confirmar = useCallback(async () => {
    setError(null);
    const validado = validarFormContaPagar(form);
    if (validado.ok === false) {
      setError(validado.error);
      return;
    }

    setLoading(true);
    try {
      const { status, data } = await criarContaPagar(validado.payload);
      if (status === 201 && data?.id) {
        setOpen(false);
        setFeedback({
          severity: "success",
          message: "Conta a pagar criada com sucesso.",
        });
        options?.onSuccess?.();
        return;
      }
      setError("Não foi possível criar a conta a pagar.");
    } catch (e: unknown) {
      const msg =
        e instanceof Error ? e.message : "Falha ao criar conta a pagar.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [form, options]);

  return {
    open,
    form,
    setForm,
    loading,
    error,
    feedback,
    abrir,
    fechar,
    confirmar,
    clearFeedback,
  };
}
